import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateProfile } from '../api/endpoints';
import type { UserProfile } from '../types';
import { ShieldCheck } from 'lucide-react';

interface TosConsentDialogProps {
  userProfile: UserProfile;
}

export function hasTosConsent(userProfile: UserProfile | undefined): boolean {
  if (!userProfile) return false;
  return !!userProfile.tosAcceptedAt;
}

export function TosConsentDialog({ userProfile }: TosConsentDialogProps) {
  const queryClient = useQueryClient();
  const [accepted, setAccepted] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await updateProfile({ tosAcceptedAt: new Date().toISOString() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">Nutzungsbedingungen</h2>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3 text-sm text-gray-700">
          <p className="font-medium text-gray-900">
            Willkommen bei Symbolbuch.de, {userProfile.displayName || 'Benutzer'}!
          </p>
          <p>
            Bitte lesen und bestätigen Sie die folgenden Nutzungsbedingungen, bevor Sie die Anwendung verwenden können.
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2 text-sm">
            <p>
              Die Nutzung dieser Webanwendung erfolgt auf eigene Verantwortung. Die Betreiber übernehmen keine Haftung
              für die Richtigkeit, Vollständigkeit oder Aktualität der bereitgestellten Inhalte und Funktionen.
            </p>
            <p>
              Die erstellten Kommunikationsmaterialien dürfen für pädagogische, therapeutische und private Zwecke
              verwendet werden. Eine kommerzielle Weiterverbreitung der über diese Anwendung erstellten Materialien
              ist ohne vorherige schriftliche Zustimmung der Betreiber nicht gestattet.
            </p>
            <p>
              Die Betreiber behalten sich vor, Nutzerkonten bei Verstoß gegen diese Nutzungsbedingungen
              oder bei missbräuchlicher Nutzung zu sperren oder zu löschen.
            </p>
            <p>
              Hochgeladene Bilder und Materialien verbleiben in der Verantwortung des Nutzers. Urheberrechtlich
              geschützte Inhalte (z.B. METACOM-Symbole) dürfen nur im Rahmen der jeweiligen Lizenzbedingungen
              verwendet werden.
            </p>
          </div>

          <label className="flex items-start gap-3 mt-4 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">
              Ich habe die Nutzungsbedingungen gelesen und akzeptiere sie.
            </span>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            disabled={!accepted || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
            className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saveMutation.isPending ? 'Wird gespeichert…' : 'Bestätigen und fortfahren'}
          </button>
        </div>
      </div>
    </div>
  );
}
